import { useState } from 'react';
import type { Position, Profile } from '../types';
import { X, Save, Trash2, Users, Calendar, Hash, Tag, AlertTriangle, Globe, Building2, Briefcase, FileText, Coffee, Plus } from 'lucide-react';

interface BudgetEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (position: Omit<Position, 'id'> & { id?: string }, memberUserIds: string[]) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  budget?: Position | null;
  position?: Position | null;
  projects?: Position[];
  profiles: Profile[];
  initialProjectId?: string;
}

export function BudgetEditModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  budget,
  position,
  profiles
}: BudgetEditModalProps) {
  const activeItem = position || budget;
  const [name, setName] = useState('');
  const [contractId, setContractId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customerDepartmentId, setCustomerDepartmentId] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [contract, setContract] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budgetDays, setBudgetDays] = useState('0');
  const [warningThresholdPercent, setWarningThresholdPercent] = useState('80');
  const [isGlobal, setIsGlobal] = useState(false);
  const [breakRelevant, setBreakRelevant] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [subTypes, setSubTypes] = useState<string[]>([]);
  const [newSubTypeInput, setNewSubTypeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [trackedState, setTrackedState] = useState<{ id?: string; isOpen: boolean }>({ isOpen: false });

  if (isOpen !== trackedState.isOpen || (isOpen && activeItem?.id !== trackedState.id)) {
    setTrackedState({ id: activeItem?.id, isOpen });
    if (isOpen) {
      if (activeItem) {
        setName(activeItem.name);
        setContractId(activeItem.contractId || '');
        setCustomerId(activeItem.customerId || '');
        setCustomerDepartmentId(activeItem.customerDepartmentId || '');
        setServiceType(activeItem.serviceType || '');
        setContract(activeItem.contract || '');
        setStartDate(activeItem.startDate || '');
        setEndDate(activeItem.endDate || '');
        setBudgetDays((activeItem.contingentDays ?? activeItem.budgetDays ?? 0).toString());
        setWarningThresholdPercent((activeItem.warningThresholdPercent ?? 80).toString());
        setIsGlobal(Boolean(activeItem.isGlobal));
        setBreakRelevant(Boolean(activeItem.breakRelevant));
        setSelectedMemberIds(activeItem.members || []);
        setSubTypes(activeItem.subTypes || []);
        setNewSubTypeInput('');
      } else {
        setName('');
        setContractId('');
        setCustomerId('');
        setCustomerDepartmentId('');
        setServiceType('');
        setContract('');
        const currentYear = new Date().getFullYear();
        setStartDate(`${currentYear}-01-01`);
        setEndDate(`${currentYear}-12-31`);
        setBudgetDays('100');
        setWarningThresholdPercent('80');
        setIsGlobal(false);
        setBreakRelevant(false);
        setSelectedMemberIds([]);
        setSubTypes([]);
        setNewSubTypeInput('');
      }
      setError(null);
    }
  }

  if (!isOpen) return null;

  const toggleMember = (userId: string) => {
    setSelectedMemberIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSelectAllMembers = () => {
    if (selectedMemberIds.length === profiles.length) {
      setSelectedMemberIds([]);
    } else {
      setSelectedMemberIds(profiles.map(p => p.id));
    }
  };

  const handleAddSubType = () => {
    const trimmed = newSubTypeInput.trim();
    if (trimmed && !subTypes.includes(trimmed)) {
      setSubTypes(prev => [...prev, trimmed]);
      setNewSubTypeInput('');
    }
  };

  const handleRemoveSubType = (index: number) => {
    setSubTypes(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please provide a position name.');
      return;
    }

    const bDays = parseFloat(budgetDays);
    if (isNaN(bDays) || bDays < 0) {
      setError('Please provide a valid contingent in working days (0 or greater).');
      return;
    }

    const threshold = parseFloat(warningThresholdPercent);
    if (bDays > 0 && (isNaN(threshold) || threshold < 1 || threshold > 100)) {
      setError('Warning threshold must be a percentage between 1 and 100.');
      return;
    }

    if (!isGlobal && (!startDate || !endDate)) {
      setError('Please select both a start date and an end date.');
      return;
    }

    if (startDate && endDate && startDate > endDate) {
      setError('Start date cannot be after end date.');
      return;
    }

    try {
      setSaving(true);
      const finalDays = isNaN(bDays) || bDays < 0 ? 0 : bDays;
      const finalThreshold = isNaN(threshold) || threshold < 1 || threshold > 100 ? 80 : threshold;
      await onSave(
        {
          ...(activeItem?.id ? { id: activeItem.id } : {}),
          name: trimmedName,
          contractId: contractId.trim(),
          customerId: customerId.trim(),
          customerDepartmentId: customerDepartmentId.trim(),
          serviceType: serviceType.trim(),
          contract: contract.trim(),
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          contingentDays: finalDays,
          budgetDays: finalDays,
          warningThresholdPercent: finalThreshold,
          isGlobal,
          breakRelevant,
          subTypes
        },
        isGlobal ? [] : selectedMemberIds
      );
      onClose();
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Failed to save position.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!activeItem?.id || !onDelete) return;
    if (!confirm(`Are you sure you want to delete position "${activeItem.name}"?`)) return;
    try {
      setDeleting(true);
      await onDelete(activeItem.id);
      onClose();
    } catch (err: unknown) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Failed to delete position.';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <h2 className="text-xl font-bold text-slate-800">
              {activeItem ? 'Edit Position' : 'Create Position'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Define position name, customer and contract identifiers, day pool contingent, and assigned staff
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="p-3.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2.5">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Left Column: Internal Attributes */}
            <div className="space-y-4">
              <div>
                <label htmlFor="budget-name" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <Tag className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  Position Name
                </label>
                <input
                  id="budget-name"
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. H2 2026 Delivery"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                />
              </div>

              <div>
                <label htmlFor="budget-service-type" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <Briefcase className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  Service Type
                </label>
                <input
                  id="budget-service-type"
                  type="text"
                  value={serviceType}
                  onChange={e => setServiceType(e.target.value)}
                  placeholder="e.g. Consulting, Development"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition font-mono"
                />
              </div>

              <div>
                <label htmlFor="budget-contract" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <FileText className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  SPF Contract ID
                </label>
                <input
                  id="budget-contract"
                  type="text"
                  value={contract}
                  onChange={e => setContract(e.target.value)}
                  placeholder="e.g. Master Services Agreement"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                />
              </div>
            </div>

            {/* Right Column: Customer Attributes */}
            <div className="space-y-4">
              <div>
                <label htmlFor="budget-customer-id" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <Building2 className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  Customer Name
                </label>
                <input
                  id="budget-customer-id"
                  type="text"
                  value={customerId}
                  onChange={e => setCustomerId(e.target.value)}
                  placeholder="e.g. CUST-1042"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition font-mono"
                />
              </div>

              <div>
                <label htmlFor="budget-customer-department-id" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <Building2 className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  Customer Department ID
                </label>
                <input
                  id="budget-customer-department-id"
                  type="text"
                  value={customerDepartmentId}
                  onChange={e => setCustomerDepartmentId(e.target.value)}
                  placeholder="e.g. DEPT-1042"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition font-mono"
                />
              </div>

              <div>
                <label htmlFor="budget-contract-id" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  <Hash className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                  Customer Order ID
                </label>
                <input
                  id="budget-contract-id"
                  type="text"
                  value={contractId}
                  onChange={e => setContractId(e.target.value)}
                  placeholder="e.g. CTR-2026-042"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition font-mono"
                />
              </div>
            </div>
          </div>

          {/* Global Position Checkbox */}
          <div className="p-3.5 bg-indigo-50/50 border border-indigo-100 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Globe className="w-4 h-4 text-indigo-600 shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-800">Global Position (All Team Members)</p>
                <p className="text-[11px] text-slate-500">Accessible to all users for vacation, overhead, or internal time</p>
              </div>
            </div>
            <input
              type="checkbox"
              id="is-global-pos"
              checked={isGlobal}
              onChange={e => setIsGlobal(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
            />
          </div>

          {/* Break Relevant Checkbox */}
          <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Coffee className="w-4 h-4 text-indigo-600 shrink-0" />
              <div>
                <p className="text-xs font-bold text-slate-800">Break Relevant (Swiss Labor Law)</p>
                <p className="text-[11px] text-slate-500">Subject to statutory break requirements; prioritized when breaking ties</p>
              </div>
            </div>
            <input
              type="checkbox"
              id="is-break-relevant"
              checked={breakRelevant}
              onChange={e => setBreakRelevant(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="budget-start-date" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <Calendar className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                Start Date
              </label>
              <input
                id="budget-start-date"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                required
              />
            </div>

            <div>
              <label htmlFor="budget-end-date" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                <Calendar className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                End Date
              </label>
              <input
                id="budget-end-date"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                required
              />
            </div>
          </div>

          {/* Budget Days & Threshold */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="budget-days" className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                Total Budget (Working Days)
              </label>
              <input
                id="budget-days"
                type="number"
                step="0.5"
                min="0"
                value={budgetDays}
                onChange={e => setBudgetDays(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                required
              />
              <p className="text-[11px] text-slate-400 mt-1">Set to 0 for unbounded tracking (no limits or warnings).</p>
            </div>

            <div>
              <label htmlFor="budget-warning-threshold" className={`block text-xs font-semibold uppercase tracking-wider mb-1.5 ${parseFloat(budgetDays) === 0 ? 'text-slate-400' : 'text-slate-600'}`}>
                Warning Threshold (%)
              </label>
              <input
                id="budget-warning-threshold"
                type="number"
                step="1"
                min="1"
                max="100"
                disabled={parseFloat(budgetDays) === 0}
                value={warningThresholdPercent}
                onChange={e => setWarningThresholdPercent(e.target.value)}
                className={`w-full px-3.5 py-2.5 border border-slate-200 rounded-xl font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition ${
                  parseFloat(budgetDays) === 0 ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-slate-50 text-slate-800'
                }`}
                required={parseFloat(budgetDays) !== 0}
              />
              {parseFloat(budgetDays) === 0 && (
                <p className="text-[11px] text-slate-400 mt-1">No warnings for unbounded positions.</p>
              )}
            </div>
          </div>

          {/* Assigned Members */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <Users className="w-3.5 h-3.5 inline mr-1 text-slate-400" />
                Assigned Team Members {!isGlobal && `(${selectedMemberIds.length})`}
              </label>
              {!isGlobal && (
                <button
                  type="button"
                  onClick={handleSelectAllMembers}
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
                >
                  {selectedMemberIds.length === profiles.length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {isGlobal ? (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-500 italic">
                Global positions are automatically open to all team members. No individual assignment required.
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-1.5">
                {profiles.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-2">No user profiles available</p>
                ) : (
                  profiles.map(p => {
                    const isChecked = selectedMemberIds.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm cursor-pointer transition select-none ${
                          isChecked ? 'bg-indigo-50 text-indigo-900 font-medium' : 'hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleMember(p.id)}
                          className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="truncate">{p.email}</span>
                        {p.role === 'supervisor' && (
                          <span className="text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ml-auto">
                            Supervisor
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Sub-Types / Buchungskategorien */}
          <div className="border-t border-slate-200/80 pt-5 space-y-3">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-indigo-600" />
              <span className="text-sm font-semibold text-slate-800">Sub-Types / Buchungskategorien</span>
              <span className="text-xs text-slate-500 font-normal">(Optional)</span>
            </div>

            <p className="text-xs text-slate-500">
              Configured sub-types appear as a quick dropdown in the time entry modal and prepend to the work description (e.g. <code>[Pers. Admin] ...</code>) for grouping and filtering.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                value={newSubTypeInput}
                onChange={e => setNewSubTypeInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSubType();
                  }
                }}
                placeholder="e.g. Pers. Admin, Weiterbildung..."
                className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50/50"
              />
              <button
                type="button"
                onClick={handleAddSubType}
                disabled={!newSubTypeInput.trim()}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>

            {subTypes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {subTypes.map((st, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    <span>{st}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSubType(idx)}
                      className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                      title="Remove sub-type"
                      aria-label={`Remove ${st}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          {activeItem?.id && onDelete ? (
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition disabled:opacity-50 cursor-pointer"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || deleting}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 rounded-xl shadow-sm hover:shadow transition disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Position'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
